import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  selectCurrentUser,
  getDelegatedState,
  getCurrentUserLicence,
} from '../features/auth/authSlice';
import { isPublicPage } from '../utils/helper';
import posthog from './posthog';

/**
 * Renderless component that keeps PostHog identity in sync with Redux auth.
 * Must be rendered inside both <Provider> and <BrowserRouter>.
 *
 * Separate effects with different dependency arrays keep concerns clean:
 *
 *  Effect 1 — identity: fires when `account` changes.
 *    - Calls identify() + group() on every account change so person properties
 *      stay up to date even after profile edits (updateProfileState dispatch).
 *    - Calls reset() when account becomes null (logout / session expiry).
 *
 *  Effect 2 — session properties: fires when delegated or licence changes.
 *    - Uses posthog.register() which only touches localStorage — no HTTP call.
 *    - Skips silently when no user is present.
 *
 *  Effect 3 — session replay: anonymous visitors get no replay on public URLs
 *    (candidate traffic). Logged-in users get replay everywhere, including public pages.
 */
const PostHogUserSync = () => {
  const { pathname } = useLocation();
  const account = useSelector(selectCurrentUser);
  const delegated = useSelector(getDelegatedState);
  const licence = useSelector(getCurrentUserLicence);

  // Only used to know whether reset() should be called when account goes null.
  const wasIdentifiedRef = useRef(false);

  useEffect(() => {
    const loggedIn = Boolean(account?.id);
    if (loggedIn || !isPublicPage(pathname)) {
      posthog.startSessionRecording?.();
    } else {
      posthog.stopSessionRecording?.();
    }
  }, [pathname, account?.id]);

  // --- Effect 1: identity (only re-runs when `account` object changes) ------
  useEffect(() => {
    if (!account?.id) {
      if (wasIdentifiedRef.current) {
        posthog.reset();
        wasIdentifiedRef.current = false;
      }
      return;
    }

    const userId = String(account.id);

    const roles = Array.isArray(account.accountRoles)
      ? account.accountRoles.map((r) => r?.role?.title).filter(Boolean)
      : [];

    // Calling identify() on every account change (not just on first login)
    // ensures PostHog person properties stay in sync when the user edits their
    // profile. posthog.identify() with the same distinct_id just updates props.
    posthog.identify(userId, {
      email: account.email,
      name: [account.firstName, account.lastName].filter(Boolean).join(' '),
      first_name: account.firstName,
      last_name: account.lastName,
      job_title: account.jobTitle,
      account_type: account.accountType,
      roles,
    });

    if (account.company?.id) {
      posthog.group('company', String(account.company.id), {
        name: account.company.name,
        company_id: account.company.id,
      });
    }

    wasIdentifiedRef.current = true;
  }, [account]);

  // --- Effect 2: session-level super-properties (no HTTP call) ---------------
  useEffect(() => {
    if (!account?.id) return;

    const superProps = {
      is_delegated: Boolean(delegated),
    };

    if (delegated) {
      // delegated is a string/number id from URL params. Guard against the
      // unlikely case it was stored as an object via JSON.stringify fallback.
      superProps.delegated_into_account_id =
        typeof delegated === 'object'
          ? JSON.stringify(delegated)
          : String(delegated);
    }

    if (licence?.data?.accepted !== undefined) {
      superProps.licence_accepted = licence.data.accepted === 1;
    }

    posthog.register(superProps);
  }, [account?.id, delegated, licence]);

  return null;
};

export default PostHogUserSync;
